import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Horizontal overflow with a scrollbar at the top (and the usual one at the
 * bottom). The top bar only appears when the content is actually wider than
 * the container.
 */
export default function HorizontalScroll({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const [contentWidth, setContentWidth] = useState(0);
  const [needsScroll, setNeedsScroll] = useState(false);

  const measure = useCallback(() => {
    const bottom = bottomRef.current;
    if (!bottom) return;
    const width = bottom.scrollWidth;
    setContentWidth(width);
    setNeedsScroll(width > bottom.clientWidth + 1);
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (bottomRef.current) ro.observe(bottomRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, children]);

  const onTopScroll = () => {
    if (syncing.current || !topRef.current || !bottomRef.current) return;
    syncing.current = true;
    bottomRef.current.scrollLeft = topRef.current.scrollLeft;
    syncing.current = false;
  };

  const onBottomScroll = () => {
    if (syncing.current || !topRef.current || !bottomRef.current) return;
    syncing.current = true;
    topRef.current.scrollLeft = bottomRef.current.scrollLeft;
    syncing.current = false;
  };

  return (
    <div className={`min-w-0 ${className}`}>
      {needsScroll && (
        <div
          ref={topRef}
          onScroll={onTopScroll}
          className="horizontal-scroll overflow-x-auto overflow-y-hidden"
          aria-hidden
        >
          <div style={{ width: contentWidth, height: 1 }} />
        </div>
      )}
      <div
        ref={bottomRef}
        onScroll={onBottomScroll}
        className="horizontal-scroll overflow-x-auto min-w-0"
      >
        <div ref={innerRef} className="min-w-min">
          {children}
        </div>
      </div>
    </div>
  );
}
